import { useApi } from '../../api/api-context';
import { useHistoryStore } from '../../history/history-store';
import { useQueryMutation } from '../../api/use-query-mutation';
import { useBudgetId } from '../../budget/budget-store';
import { useQueryClient } from '@tanstack/react-query';
import { logAndThrowError } from '../../utils/errors';
// Custom hook for both create and delete operations
export const useBucketMutations = (options) => {
    const api = useApi();
    const budgetId = useBudgetId();
    const queryClient = useQueryClient();
    const queryKey = ['buckets', budgetId];
    const historyStore = useHistoryStore();
    // API function definitions
    const createBucketApi = async (data) => {
        await api.post('/bucket', data);
    };
    const deleteBucketApi = async (data) => {
        await api.delete(`/bucket/${data.budgetId}/${data.id}`);
    };
    // Create mutation
    const createOnMutate = (oldData, request) => {
        const newBucket = { ...request };
        // If no existing data, just return the new bucket
        if (!oldData) {
            return [newBucket];
        }
        // Find correct position within the category
        const sortedBuckets = [...oldData];
        const insertIndex = sortedBuckets.findIndex((bucket) => bucket.categoryId === request.categoryId && bucket.position > newBucket.position);
        if (insertIndex === -1) {
            // If no bucket in the same category has a higher position, add to the end
            return [...sortedBuckets, newBucket];
        }
        else {
            // Insert at the correct position
            return [...sortedBuckets.slice(0, insertIndex), newBucket, ...sortedBuckets.slice(insertIndex)];
        }
    };
    const { mutate: createMutate, isPending: isCreatePending } = useQueryMutation(createBucketApi, queryKey, createOnMutate, options);
    // Delete mutation
    const deleteOnMutate = (oldData, request) => {
        if (!oldData) {
            logAndThrowError('No buckets data found');
        }
        const bucketToDelete = oldData.find((bucket) => bucket.id === request.id);
        if (!bucketToDelete) {
            logAndThrowError('Bucket not found');
        }
        return oldData.filter((bucket) => bucket.id !== request.id);
    };
    const { mutate: deleteMutate, isPending: isDeletePending } = useQueryMutation(deleteBucketApi, queryKey, deleteOnMutate, options);
    // Create with history
    const createWithHistory = async (request) => {
        createMutate(request);
        historyStore.pushEvent({
            description: `Create Bucket`,
            undo: () => {
                deleteMutate({ id: request.id, budgetId });
            },
            redo: () => {
                createMutate(request);
            },
        });
    };
    // Delete with history
    const deleteWithHistory = async (request) => {
        const oldBuckets = queryClient.getQueryData(queryKey);
        const bucketToDelete = oldBuckets?.find((bucket) => bucket.id === request.id);
        if (!bucketToDelete) {
            logAndThrowError('Bucket not found');
        }
        const restoreRequest = {
            id: bucketToDelete.id,
            budgetId: bucketToDelete.budgetId,
            categoryId: bucketToDelete.categoryId,
            name: bucketToDelete.name,
            notes: bucketToDelete.notes,
            position: bucketToDelete.position,
            assignments: bucketToDelete.assignments,
        };
        deleteMutate(request);
        historyStore.pushEvent({
            description: `Delete Bucket ${bucketToDelete.name}`,
            undo: () => {
                createMutate(restoreRequest);
            },
            redo: () => {
                deleteMutate(request);
            },
        });
    };
    return {
        createBucket: createWithHistory,
        deleteBucket: deleteWithHistory,
        isCreatePending,
        isDeletePending,
    };
};
// Convenience hooks for individual operations
export const useCreateBucketApi = (options) => {
    const { createBucket, isCreatePending } = useBucketMutations(options);
    return {
        mutate: createBucket,
        isPending: isCreatePending,
    };
};
export const useDeleteBucketApi = (options) => {
    const { deleteBucket, isDeletePending } = useBucketMutations(options);
    return {
        mutate: deleteBucket,
        isPending: isDeletePending,
    };
};
